import { useMemo } from 'react';

import type { FAQPage, Question } from 'schema-dts';

import type { Locale } from '@change-corgi/config/locales';
import { isSupportedLocale } from '@change-corgi/config/locales';

import { useTopicPageData } from 'src/app/pages/topic/pageContext';
import { jsonLd } from 'src/app/shared/utils/jsonLd';

type Result = ModelHookResult<{
	image: string;
	title: string;
	slug: string;
	lang: string | false;
	structuredDataJsonLd: string | undefined;
}>;

function upperCaseFirstChar(s: string) {
	return s.charAt(0).toUpperCase() + s.substring(1).toLowerCase();
}

function extractLang(slug: string): string | false {
	const regex: RegExp = /-([a-z]{2})-([a-z]{2})$/i;
	const match = slug.match(regex);

	if (match) {
		const lang = `${match[1].toLowerCase()}-${match[2].toUpperCase()}`;

		if (isSupportedLocale(lang as Locale)) {
			return lang;
		}
	}

	return false;
}

export function usePageMeta(): Result {
	const {
		data: { name, photos, slug, structuredData },
	} = useTopicPageData();

	const lang = extractLang(slug);

	const title = upperCaseFirstChar(name);

	const structuredDataLd = useMemo(() => {
		if (!structuredData?.length) return undefined;

		const questions = structuredData.map(({ jsonLdData }) => JSON.parse(jsonLdData) as Question);

		return questions.length
			? jsonLd<FAQPage>({
					'@context': 'https://schema.org',
					'@type': 'FAQPage',
					mainEntity: questions,
				})
			: undefined;
	}, [structuredData]);

	return {
		data: {
			image: photos[0]?.heroMedium.url ?? 'https://static.change.org/images/default-petition.gif',
			title,
			lang,
			slug,
			structuredDataJsonLd: structuredDataLd,
		},
	};
}
